import { Chip, Stack } from '@mui/material';
import { Tool } from '@procureai/agents';
import { prop, uniqBy } from 'ramda';
import { useTranslation } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolErrorIndicator } from '../ChatMessage/indicators/ChatToolErrorIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { I18nNamespace } from '@/legacy/common/types';

import { ToolResultComponent } from '.';

type SearchCommodityGroupInput = {
  query?: string[];
  limit?: number;
};

type CommodityGroupItem = {
  id: string;
  name: string;
  displayCode: string | null;
  sapSourceReference: string | null;
};

type CommodityGroupQueryGroup = {
  query: string;
  foundCount: number;
  items: CommodityGroupItem[];
};

type SearchCommodityGroupOutput = {
  status: 'ok' | 'no_sap_access';
  message?: string | null;
  groupedItems: CommodityGroupQueryGroup[];
  error?: string | null;
};

const formatQuery = (query: string[] | undefined): string => {
  if (!query || query.length === 0) return '*';
  return query.join(', ');
};

const dedupeById = uniqBy(prop('id'));

const MAX_DISPLAYED_CHIPS = 5;

export const SearchCommodityGroupTool: ToolResultComponent<
  Tool.searchCommodityGroup,
  SearchCommodityGroupInput,
  SearchCommodityGroupOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const query = formatQuery(tool.input?.query);

  switch (tool.state) {
    case 'input-streaming':
    case 'input-available':
      return <ChatLoadingIndicator message={t('call', { query })} />;

    case 'output-error':
      return <ChatToolErrorIndicator message={t('error')} />;

    case 'output-available': {
      if (tool.output.status === 'no_sap_access') {
        return <ChatToolResultIndicator message={t('noSapAccess')} defaultOpen={false} disabled={true} />;
      }
      if (tool.output.error) {
        return <ChatToolErrorIndicator message={t('error')} />;
      }

      const allItems = dedupeById((tool.output.groupedItems ?? []).flatMap((g) => g.items));
      const count = allItems.length;

      if (count === 0) {
        return <ChatToolResultIndicator message={t('resultEmpty', { query })} defaultOpen={false} disabled={true} />;
      }

      const visibleItems = allItems.slice(0, MAX_DISPLAYED_CHIPS);
      const overflow = count - visibleItems.length;

      return (
        <ChatToolResultIndicator message={t('result', { count, query })} defaultOpen={true} disabled={false}>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ p: 2 }}>
            {visibleItems.map((item) => {
              const code = item.sapSourceReference ?? item.displayCode;
              const label = code ? `${item.name} (${code})` : item.name;
              return <Chip key={item.id} label={label} variant="outlined" size="small" />;
            })}
            {overflow > 0 ? <Chip label={t('more', { count: overflow })} variant="outlined" size="small" /> : null}
          </Stack>
        </ChatToolResultIndicator>
      );
    }
  }
};
